import { Component, computed, effect, inject, input, OnDestroy, OnInit } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormRadioButtonComponent, FormSelectComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { PricePipe } from '@shared/pipes';
import { BillingTreatment, LicenseFacade } from '@app/store/license';
import { PackageFacade } from '@app/store/package';
import { resolvePricingTiers } from './resolve-pricing-tiers';

@Component({
    selector: 'app-license-upgrade-modal',
    templateUrl: './upgrade.component.html',
    styleUrls: ['./upgrade.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormSelectComponent, FormRadioButtonComponent, UnavailableComponent, PricePipe],
    providers: [PricePipe],
})
export class UpgradeComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly packageFacade = inject(PackageFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly pricePipe = inject(PricePipe);
    private readonly translateService = inject(TranslateService);

    readonly id = input.required<string>();

    readonly license = this.licenseFacade.license;
    readonly saving = this.licenseFacade.saving;
    readonly error = computed(() => this.licenseFacade.error() ?? this.packageFacade.error());
    readonly loading = computed(() => this.licenseFacade.loading() ?? this.packageFacade.loading());

    readonly form = this.formBuilder.group({
        pricingTierId: this.formBuilder.control('', [FormValidators.Required]),
        billingTreatment: this.formBuilder.control<BillingTreatment>('proRataRemainingTerm', [FormValidators.Required]),
    });

    readonly tierOptions = computed(() => {
        const license = this.license();
        const pkg = this.packageFacade.package();
        if (!license || !pkg) return [];

        const options = resolvePricingTiers(license, pkg).map(({ label, id, prices }) => ({
            label: `${label} · ${this.pricePipe.transform(prices[license.currency], license.currency)}`,
            value: id,
        }));

        // The license's current tier can be missing from the package's pricing list (tiers change over
        // time). Add it manually so the dropdown reflects the current selection instead of staying empty.
        const { sourceId, label, price } = license.pricing;
        if (!options.some((option) => option.value === sourceId)) {
            options.unshift({
                label: `${label} · ${this.pricePipe.transform(price, license.currency)}`,
                value: sourceId,
            });
        }

        return options;
    });

    readonly billingOptions = [
        { label: this.translateService.instant('licenses.billing.proRataRemainingTerm'), value: 'proRataRemainingTerm' },
        { label: this.translateService.instant('licenses.billing.fullPriceImmediately'), value: 'fullPriceImmediately', disabled: true },
        { label: this.translateService.instant('licenses.billing.noChargeUntilRenewal'), value: 'noChargeUntilRenewal', disabled: true },
    ];

    constructor() {
        const getRelatedPackage = effect(() => {
            const license = this.license();
            if (!license) return;

            this.packageFacade.getPackage(license.packageId);
            getRelatedPackage.destroy();
        });

        effect(() => {
            const options = this.tierOptions();
            const license = this.license();
            if (!license || options.length === 0 || this.form.controls.pricingTierId.value) return;

            const current = options.find((option) => option.value === license.pricing.sourceId);
            if (!current) return;

            this.form.controls.pricingTierId.setValue(current.value);
        });

        effect(() => {
            if (this.licenseFacade.saved()) {
                this.close();
            }
        });
    }

    ngOnInit() {
        this.licenseFacade.getLicense(this.id());
    }

    ngOnDestroy(): void {
        this.licenseFacade.clear();
        this.packageFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const value = this.form.getRawValue();

        if (this.form.valid) {
            this.licenseFacade.upgradeLicense(this.id(), value);
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }], { relativeTo: this.route.parent?.parent });
    }
}
